import * as React from 'react';
import { FC, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Tooltip, IconButton } from '@mui/material';
import RepeatIcon from '@material-ui/icons/Repeat';
import CheckIcon from '@material-ui/icons/Check';
import WarningIcon from '@material-ui/icons/Warning';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { DateField } from '../../../components/Forms/fields/DateField';
import { TimeField } from '../../../components/Forms/fields/TimeField';
import { TextInputField } from '../../../components/Forms/fields/TextInputField';
import { NumberInputField } from '../../../components/Forms/fields/NumberInputField';
import { SelectTextField } from '../../../components/Forms/fields/SelectTextField';
import { AppointmentData, DateInterval, ProfessionalData, RecurringConfig, RecurringUnit } from '../../../models/api-models';
import { ComboField } from '../../../components/Forms/fields';
import moment from 'moment';
import AppButton from '../../../components/AppButton';
import { getAppointmentsAsync } from '../../../ajax/requests';

export interface AbsenceModalProps {
    initialAbsence: DateInterval;
    isEdit: boolean;
    onSave: (absence: DateInterval, selectedProfessional?: ProfessionalData) => Promise<void>;
    onDelete?: () => Promise<void>;
    onClose: () => void;
    professionals?: ProfessionalData[];
    initialProfessionalId?: string;
}

export const AbsenceModal: FC<AbsenceModalProps> = ({ initialAbsence, isEdit, onSave, onDelete, onClose, professionals, initialProfessionalId }) => {
    const t = useTranslations();

    const [absence, setAbsence] = useState<DateInterval>(initialAbsence);
    const [editRecurring, setEditRecurring] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [checkingConflicts, setCheckingConflicts] = useState(false);
    const [conflictingAppointments, setConflictingAppointments] = useState<AppointmentData[] | null>(null);
    const [selectedProfessionalId, setSelectedProfessionalId] = useState<string | undefined>(initialProfessionalId);

    const fromDate = absence.from.substring(0, 10);
    const fromTime = absence.from.length >= 16 ? absence.from.substring(11, 16) : '00:00';
    const toDate = absence.to.substring(0, 10);
    const toTime = absence.to.length >= 16 ? absence.to.substring(11, 16) : '23:59';

    const setFromDate = (d: string) => setAbsence(a => ({ ...a, from: `${d}T${fromTime}:00` }));
    const setFromTime = (t: string) => setAbsence(a => ({ ...a, from: `${fromDate}T${t}:00` }));
    const setToDate = (d: string) => setAbsence(a => ({ ...a, to: `${d}T${toTime}:00` }));
    const setToTime = (t: string) => setAbsence(a => ({ ...a, to: `${toDate}T${t}:00` }));

    const periodUnitsToEnum: Record<string, RecurringUnit> = {
        [t(TK.days)]: RecurringUnit.Days,
        [t(TK.weeks)]: RecurringUnit.Weeks,
        [t(TK.months)]: RecurringUnit.Months,
        [t(TK.day)]: RecurringUnit.Days,
        [t(TK.week)]: RecurringUnit.Weeks,
        [t(TK.month)]: RecurringUnit.Months,
    };

    const periodUnitsLabelPlural: Record<RecurringUnit, string> = {
        [RecurringUnit.Days]: t(TK.days),
        [RecurringUnit.Weeks]: t(TK.weeks),
        [RecurringUnit.Months]: t(TK.months),
    };

    const periodUnitsLabelSingular: Record<RecurringUnit, string> = {
        [RecurringUnit.Days]: t(TK.day),
        [RecurringUnit.Weeks]: t(TK.week),
        [RecurringUnit.Months]: t(TK.month),
    };

    const recurringLabel = (r: RecurringConfig): string => {
        const unitTK = r.unit === RecurringUnit.Days
            ? (r.value ?? 1) === 1 ? TK.day : TK.days
            : r.unit === RecurringUnit.Weeks
                ? (r.value ?? 1) === 1 ? TK.week : TK.weeks
                : (r.value ?? 1) === 1 ? TK.month : TK.months;
        const str = `${t(TK.every)} ${r.value} ${t(unitTK).toLowerCase()}`;
        return r.endDate ? `${str}, ${t(TK.until).toLowerCase()} ${r.endDate}` : str;
    };

    const toggleRecurring = () => {
        if (absence.recurring) {
            setAbsence(a => ({ ...a, recurring: undefined }));
            setEditRecurring(false);
        } else {
            const defaultRecurring: RecurringConfig = {
                endDate: moment(absence.from).add(1, 'month').format('YYYY-MM-DD'),
                unit: RecurringUnit.Weeks,
                value: 1,
            };
            setAbsence(a => ({ ...a, recurring: defaultRecurring }));
            setEditRecurring(true);
        }
    };

    const updateRecurring = (patch: Partial<RecurringConfig>) =>
        setAbsence(a => ({ ...a, recurring: { ...a.recurring!, ...patch } }));

    const handleSave = async (force = false) => {
        if (!force) {
            setCheckingConflicts(true);
            try {
                const appointments = await getAppointmentsAsync(absence.from.substring(0, 10), absence.to.substring(0, 10), undefined, true);
                const absenceFrom = moment(absence.from);
                const absenceTo = moment(absence.to).subtract(1, 'second');
                const overlapping = appointments.filter(appt => {
                    if (selectedProfessionalId && appt.professionalId !== selectedProfessionalId) return false;
                    const apptStart = moment(`${appt.date}T${appt.start}`);
                    const apptEnd = moment(`${appt.date}T${appt.end}`);
                    return apptStart.isBefore(absenceTo) && apptEnd.isAfter(absenceFrom);
                });
                if (overlapping.length > 0) {
                    setConflictingAppointments(overlapping);
                    return;
                }
            } finally {
                setCheckingConflicts(false);
            }
        }
        setSaving(true);
        try {
            const selectedProfessional = professionals?.find(p => p.id === selectedProfessionalId);
            await onSave(absence, selectedProfessional);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!onDelete) return;
        setDeleting(true);
        try {
            await onDelete();
            onClose();
        } finally {
            setDeleting(false);
        }
    };

    const busy = saving || deleting || checkingConflicts;

    return (
        <Modal isOpen toggle={onClose}>
            <ModalHeader toggle={onClose}>
                {isEdit ? t(TK.absence) : t(TK.bookPersonalTime)}
            </ModalHeader>
            <ModalBody style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                <div style={{ display: 'flex', gap: 'var(--spacer-S)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <DateField label={t(TK.startDate)} value={fromDate} onChange={setFromDate} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TimeField label={t(TK.startTime)} value={fromTime} onChange={setFromTime} />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacer-S)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <DateField label={t(TK.endDate)} value={toDate} onChange={setToDate} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TimeField label={t(TK.endTime)} value={toTime} onChange={setToTime} />
                    </div>
                </div>
                {professionals && professionals.length > 0 && (
                    <ComboField
                        label={t(TK.professional)}
                        options={professionals.map(p => ({ value: p.id, label: p.name }))}
                        value={selectedProfessionalId}
                        onChange={setSelectedProfessionalId}
                    />
                )}
                <TextInputField
                    label={t(TK.description)}
                    value={absence.description}
                    onChange={description => setAbsence(a => ({ ...a, description }))}
                />

                {/* Recurring */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                        <Tooltip title={t(TK.makeItRecurring)}>
                            <IconButton
                                onClick={toggleRecurring}
                                size="small"
                                style={{ color: absence.recurring ? 'var(--appBlue)' : 'var(--text-muted)' }}
                            >
                                <RepeatIcon />
                            </IconButton>
                        </Tooltip>
                        {absence.recurring && !editRecurring && (
                            <AppButton
                                color="link"
                                style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: 0 }}
                                onClick={() => setEditRecurring(true)}
                            >
                                {recurringLabel(absence.recurring)}
                            </AppButton>
                        )}
                    </div>
                    {absence.recurring && editRecurring && (
                        <div style={{
                            backgroundColor: 'var(--bg-light)',
                            borderRadius: 4,
                            border: 'var(--border-thin) solid var(--appLigthGrey)',
                            padding: 'var(--spacer-S)',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 'var(--spacer-XS)',
                            alignItems: 'center',
                            marginTop: 'var(--spacer-XS)',
                        }}>
                            <NumberInputField
                                prepend={t(TK.every)}
                                type="number"
                                min={1}
                                style={{ maxWidth: 110 }}
                                value={absence.recurring.value}
                                onChange={value => updateRecurring({ value: value ?? 1 })}
                            />
                            <SelectTextField
                                style={{ maxWidth: 150 }}
                                value={absence.recurring.value > 1
                                    ? periodUnitsLabelPlural[absence.recurring.unit]
                                    : periodUnitsLabelSingular[absence.recurring.unit]}
                                options={absence.recurring.value > 1
                                    ? Object.values(periodUnitsLabelPlural)
                                    : Object.values(periodUnitsLabelSingular)}
                                onValueChanged={label => updateRecurring({ unit: periodUnitsToEnum[label] })}
                            />
                            <DateField
                                label={t(TK.until)}
                                style={{ maxWidth: 150 }}
                                value={absence.recurring.endDate || ''}
                                type="date"
                                onChange={endDate => updateRecurring({ endDate })}
                            />
                            <AppButton outline color="success" onClick={() => setEditRecurring(false)}>
                                <CheckIcon />
                            </AppButton>
                        </div>
                    )}
                </div>
                {conflictingAppointments && conflictingAppointments.length > 0 && (
                    <div style={{ backgroundColor: 'var(--warning-bg)', border: 'var(--border-thin) solid var(--warning-border)', borderRadius: 4, padding: 'var(--spacer-S)', display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', color: 'var(--warning-color)', fontWeight: 600 }}>
                            <WarningIcon style={{ fontSize: '1.2rem' }} />
                            {t(TK.conflictingAppointmentsWarning)}
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 'var(--spacer-M)', display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XXS)' }}>
                            {conflictingAppointments.map(appt => (
                                <li key={appt.id} style={{ fontSize: '0.875rem' }}>
                                    {appt.date} {appt.start}–{appt.end}
                                    {appt.clientName && ` · ${appt.clientName}`}
                                    {appt.type && ` · ${appt.type}`}
                                    {appt.professionalName && ` · ${appt.professionalName}`}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                {!conflictingAppointments ? (<>
                    {isEdit && onDelete && (
                        <AppButton color="danger" outline onClick={handleDelete} disabled={busy} style={{ marginRight: 'auto' }}>
                            {t(TK.remove)}
                        </AppButton>
                    )}
                    <AppButton color="secondary" onClick={onClose} disabled={busy}>{t(TK.cancel)}</AppButton>
                    <AppButton color="success" onClick={() => handleSave()} disabled={busy}>{t(TK.save)}</AppButton>
                </>) : (<>
                    <AppButton color="secondary" onClick={() => setConflictingAppointments(null)} disabled={busy} style={{ marginRight: 'auto' }}>
                        {t(TK.cancel)}
                    </AppButton>
                    <AppButton color="warning" onClick={() => handleSave(true)} disabled={busy}>{t(TK.bookAnyway)}</AppButton>
                </>)}
            </ModalFooter>
        </Modal>
    );
};
